/**
 * ArchivedAccountsSection Component
 * 
 * Displays archived accounts grouped by account group in an accordion.
 */

import React from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import CopyFeedbackTooltip from "../../../components/CopyFeedbackTooltip";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArchiveIcon from "@mui/icons-material/Archive";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Account } from "../../../types";
import { AccountGroupDoc } from "../hooks/useAccountGroups";

export interface ArchivedAccountsSectionProps {
  archivedAccounts: Account[];
  accountGroups: AccountGroupDoc[];
  groupedArchivedAccounts: Record<string, Account[]>;
  getArchivedGroupBalance: (groupName: string) => number;
  getAccountDetailsForDisplay: (account: Account) => Array<{ name: string; value: string | number }>;
  expandedAccountDetails: Set<string>;
  onToggleDetails: (accountId: string) => void;
  onCopyToClipboard: (text: string, feedbackKey?: string) => Promise<void>;
  lastCopiedKey: string | null;
  isMobile: boolean;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, account: Account) => void;
  formatCurrency: (amount: number, currency: string) => string;
  valuesVisible: boolean;
  defaultCurrency: string;
}

/**
 * Archived accounts section component
 */
export default function ArchivedAccountsSection({
  archivedAccounts,
  accountGroups,
  groupedArchivedAccounts,
  getArchivedGroupBalance,
  getAccountDetailsForDisplay,
  expandedAccountDetails,
  onToggleDetails,
  onCopyToClipboard,
  lastCopiedKey,
  isMobile,
  onMenuOpen,
  formatCurrency,
  valuesVisible,
  defaultCurrency,
}: ArchivedAccountsSectionProps): React.ReactElement {
  return (
    <Accordion defaultExpanded={false} disableGutters sx={{ marginTop: 1, "& .MuiAccordionSummary-root": { minHeight: { xs: 40, sm: 28 }, py: { xs: 0.25, sm: 0 } }, "&:before": { display: "none" } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
        <Typography variant="subtitle2" fontWeight="medium" color="text.secondary">
          Archived Accounts {archivedAccounts.length > 0 && `(${archivedAccounts.length})`}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0.5, py: 0.5, pt: 0 }}>
        {archivedAccounts.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: 1,
              borderColor: "divider",
              borderRadius: 0.5,
              textAlign: "center",
            }}
          >
            <Stack spacing={0.5} alignItems="center">
              <ArchiveIcon sx={{ fontSize: 36, color: "text.secondary", opacity: 0.5 }} />
              <Typography variant="subtitle1" color="text.secondary">
                No archived accounts
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 320 }}>
                Archive from the account menu when it has no transactions and zero balance.
              </Typography>
            </Stack>
          </Paper>
        ) : (
          <Stack spacing={0.25 / 2}>
            {accountGroups.map((group) => {
              const groupAccounts = groupedArchivedAccounts[group.name];
              if (!groupAccounts || groupAccounts.length === 0) return null;

              const archivedGroupBalance = getArchivedGroupBalance(group.name);
              return (
                <Accordion
                  key={`archived-${group.id}`}
                  defaultExpanded
                  disableGutters
                  sx={{
                    "& .MuiAccordionSummary-root": { minHeight: { xs: 44, sm: 32 }, py: { xs: 0.5, sm: 0.25 } },
                    "&:before": { display: "none" },
                    marginTop: 0,
                    marginBottom: 0,
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", pr: 0.5, gap: 1 }}>
                      <Typography variant="subtitle2" fontWeight="medium" sx={{ opacity: 0.85 }}>
                        {group.name} ({groupAccounts.length})
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", minWidth: 80, opacity: 0.85 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.65rem", lineHeight: 1.2, textTransform: "uppercase", letterSpacing: "0.02em", fontWeight: 500 }}
                        >
                          Balance
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color={archivedGroupBalance >= 0 ? "primary.main" : "error.main"}
                          sx={{ lineHeight: 1.2, fontSize: "0.85rem", fontFamily: "inherit" }}
                        >
                          {valuesVisible ? formatCurrency(archivedGroupBalance, defaultCurrency) : "•••••"}
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0.5, py: 0.25, pt: 0 }}>
                    <Stack spacing={0.25}>
                      {groupAccounts.map((account) => (
                        <Paper
                          key={account.id}
                          elevation={0}
                          sx={{
                            p: 0.75,
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 0.5,
                            opacity: 0.9,
                            "&:hover": { boxShadow: 1, opacity: 1 },
                            transition: "box-shadow 0.2s, opacity 0.2s",
                          }}
                        >
                          <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="space-between">
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Typography variant="body2" fontWeight="bold" sx={{ lineHeight: 1.3 }}>
                                  {account.accountName}
                                </Typography>
                                {account.details !== undefined && (() => {
                                  const group = accountGroups.find((g) => g.name === account.group);
                                  return group?.details && group.details.length > 0;
                                })() && (
                                  <IconButton
                                    size="small"
                                    onClick={() => onToggleDetails(account.id)}
                                    sx={{
                                      p: 0.25,
                                      minWidth: "auto",
                                      width: 24,
                                      height: 24,
                                      transition: "transform 0.2s",
                                      transform: expandedAccountDetails.has(account.id) ? "rotate(180deg)" : "rotate(0deg)",
                                    }}
                                  >
                                    <ExpandMoreIcon sx={{ fontSize: "1rem" }} />
                                  </IconButton>
                                )}
                              </Box>
                              {account.description && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.15, display: "block", lineHeight: 1.2 }}>
                                  {account.description}
                                </Typography>
                              )}
                              {account.details !== undefined && getAccountDetailsForDisplay(account).length > 0 && expandedAccountDetails.has(account.id) && (() => {
                                const group = accountGroups.find((g) => g.name === account.group);
                                const maxNameLength = group?.details ? Math.max(...group.details.map(d => d.name.length), 10) : 10;
                                const labelWidth = Math.max(maxNameLength * 8 + 20, 100);
                                return (
                                  <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                    {getAccountDetailsForDisplay(account).map((detail) => (
                                      <Box
                                        key={detail.name}
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.75,
                                          ...(detail.value !== "-" && {
                                            "&:hover .copy-icon": {
                                              opacity: 1,
                                            },
                                          }),
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ 
                                            width: labelWidth,
                                            minWidth: labelWidth,
                                            fontWeight: 500, 
                                            fontSize: "0.875rem",
                                            flexShrink: 0
                                          }}
                                        >
                                          {detail.name}:
                                        </Typography>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1, minWidth: 0 }}>
                                          <Typography
                                            variant="body2"
                                            color={detail.value === "-" ? "text.secondary" : "text.primary"}
                                            sx={{ fontFamily: "monospace", fontSize: "0.875rem", fontStyle: detail.value === "-" ? "italic" : "normal" }}
                                          >
                                            {String(detail.value)}
                                          </Typography>
                                          {detail.value !== "-" && (() => {
                                            const copyKey = `archived-${account.id}-${detail.name}`;
                                            return (
                                              <CopyFeedbackTooltip copyLabel="Copy" feedbackKey={copyKey} lastCopiedKey={lastCopiedKey} isMobile={isMobile}>
                                                <IconButton
                                                  size="small"
                                                  onClick={() => onCopyToClipboard(String(detail.value), copyKey)}
                                                  className="copy-icon"
                                                  sx={{
                                                    opacity: 0,
                                                    transition: "opacity 0.2s",
                                                    p: 0.125,
                                                    ml: 0.25,
                                                    minWidth: "auto",
                                                    width: 20,
                                                    height: 20,
                                                    flexShrink: 0,
                                                    "&:hover": {
                                                      opacity: 1,
                                                    },
                                                  }}
                                                >
                                                  <ContentCopyIcon sx={{ fontSize: "0.875rem" }} />
                                                </IconButton>
                                              </CopyFeedbackTooltip>
                                            );
                                          })()}
                                        </Box>
                                      </Box>
                                    ))}
                                  </Stack>
                                );
                              })()}
                            </Box>

                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                              <Chip label="Archived" size="small" color="default" sx={{ fontSize: "0.65rem", height: 18 }} />
                              <Typography
                                variant="body2"
                                fontWeight="bold"
                                color={
                                  valuesVisible && parseFloat(String(account.amount || 0)) >= 0
                                    ? "primary.main"
                                    : valuesVisible && parseFloat(String(account.amount || 0)) < 0
                                    ? "error.main"
                                    : "text.secondary"
                                }
                                sx={{ minWidth: 72, textAlign: "right", lineHeight: 1.2, fontSize: "0.8125rem" }}
                              >
                                {valuesVisible
                                  ? formatCurrency(
                                      parseFloat(String(account.amount || 0)),
                                      account.currency
                                    )
                                  : "•••••"}
                              </Typography>

                              <IconButton
                                size="small"
                                onClick={(e) => onMenuOpen(e, account)}
                                sx={{ flexShrink: 0, p: 0.25 }}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
